import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useProject } from '@/hooks/useProjects';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useSecrets, useUpdateSecret, useDeleteSecret } from '@/hooks/useSecrets';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchBar } from '@/components/ui/SearchBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { Plus, Eye, EyeOff, Copy, Edit, MoreHorizontal, Trash, HomeIcon, BuildingIcon } from 'lucide-react';
import { SecretDrawer } from '@/components/secrets/SecretDrawer';
import { useToast } from '@/hooks/useToast';
import { ResourcePageHeader } from '@/components/layout/ResourcePageHeader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ApiSecret } from '@envase/api/client';

export const Route = createFileRoute('/_authenticated/org/$orgId/projects/$id/secrets')({
  component: ProjectSecretsPage,
});

function ProjectSecretsPage() {
  const { id: projectId, orgId } = Route.useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const { organization } = useOrganization(orgId);
  const { project } = useProject(projectId, organization?.id);
  const environmentsQuery = useEnvironments(projectId);
  const secretsQuery = useSecrets(projectId);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [editingSecret, setEditingSecret] = useState<ApiSecret | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState<ApiSecret | null>(null);
  const [revealedSecrets, setRevealedSecrets] = useState<Set<string>>(new Set());

  const updateSecretMutation = useUpdateSecret(projectId);
  const deleteSecretMutation = useDeleteSecret(projectId);

  const environments = environmentsQuery.data?.environments || [];
  const secrets = secretsQuery.data?.secrets || [];

  const filteredSecrets = secrets.filter((secret) =>
    secret.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (secret.description?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const toggleReveal = (secretId: string) => {
    setRevealedSecrets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(secretId)) {
        newSet.delete(secretId);
      } else {
        newSet.add(secretId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (value: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('toast.success.copied'));
    } catch {
      toast.error(t('toast.error.copyFailed'));
    }
  };

  const getEnvironmentName = (environmentId: string | null) => {
    if (!environmentId) return 'Unknown';
    const env = environments.find((e) => e.id === environmentId);
    return env?.name || 'Unknown';
  };

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: project?.name || 'Project',
      to: `/org/${orgId}/projects/${projectId}`,
    },
    {
      name: t('secrets.title'),
      to: `/org/${orgId}/projects/${projectId}/secrets`,
    },
  ];

  return (
    <div className="space-y-8">
      <ResourcePageHeader
        breadcrumbs={breadcrumbs}
        title={`${project?.name} - ${t('secrets.title')}`}
        subtitle={t('secrets.description')}
        action={
          <Button onClick={() => setShowCreateDrawer(true)} data-testid="create-secret-button">
            <Plus className="w-4 h-4 mr-2" />
            {t('secrets.createNew')}
          </Button>
        }
        backTo={`/org/${orgId}/projects/${projectId}`}
        variant="project"
      />

      <div>
        <div className="flex items-center justify-between mb-4 gap-4">
          <SearchBar
            placeholder={t('secrets.searchPlaceholder')}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </div>

        {/* Tabla unificada */}
        {filteredSecrets.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('secrets.colKey')}</TableHead>
                <TableHead>{t('secrets.colValue')}</TableHead>
                <TableHead>{t('secrets.colDescription')}</TableHead>
                <TableHead>{t('secrets.colEnvironment')}</TableHead>
                <TableHead className="text-right">{t('secrets.colActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSecrets.map((secret) => (
                <TableRow key={secret.id}>
                  <TableCell className="font-mono text-sm max-w-[180px] truncate" title={secret.key}>{secret.key}</TableCell>
                  <TableCell className="relative">
                    <div className="flex items-center gap-2">
                      <span className={revealedSecrets.has(secret.id) ? '' : 'blur-sm select-none'}>
                        {secret.value}
                      </span>
                      <Button variant="ghost" size="icon" onClick={() => toggleReveal(secret.id)}>
                        {revealedSecrets.has(secret.id) ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => copyToClipboard(secret.value)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm max-w-[220px] truncate" title={secret.description || ''}>{secret.description}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{getEnvironmentName(secret.environmentId)}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => toggleReveal(secret.id)}>
                          {revealedSecrets.has(secret.id) ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                          {revealedSecrets.has(secret.id) ? t('secrets.hide') : t('secrets.reveal')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyToClipboard(secret.value)}>
                          <Copy className="w-4 h-4 mr-2" />
                          {t('secrets.copy')}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          data-testid={`edit-secret-${secret.key}`}
                          onClick={() => setEditingSecret(secret)}
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          {t('secrets.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600" 
                          data-testid={`delete-secret-${secret.key}`}
                          onClick={() => {
                            setSecretToDelete(secret);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash className="w-4 h-4 mr-2" />
                          {t('secrets.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            title={t('secrets.list.empty')}
            description={t('secrets.noSecretsHint')}
            action={{
              label: t('secrets.createFirst'),
              onClick: () => setShowCreateDrawer(true),
            }}
          />
        )}
      </div>

      {/* Create Secret Drawer */}
      <SecretDrawer
        open={showCreateDrawer}
        onOpenChange={setShowCreateDrawer}
        projectId={projectId}
        environmentId={environments[0]?.id}
        onSuccess={() => {
          secretsQuery.refetch();
          setShowCreateDrawer(false);
        }}
      />

      {/* Edit Secret Drawer */}
      {editingSecret && (
        <SecretDrawer
          open={!!editingSecret}
          onOpenChange={(open) => {
            if (!open) setEditingSecret(null);
          }}
          projectId={projectId}
          environmentId={editingSecret.environmentId || undefined}
          secret={{
            id: editingSecret.id,
            key: editingSecret.key,
            value: editingSecret.value,
            description: editingSecret.description || null,
            scope: editingSecret.environmentId ? 'environment' : 'project',
            environmentId: editingSecret.environmentId,
          }}
          onSuccess={() => {
            secretsQuery.refetch();
            setEditingSecret(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Secret</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the secret "{secretToDelete?.key}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSecretToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (secretToDelete) {
                  try {
                    await deleteSecretMutation.mutateAsync({
                      secretId: secretToDelete.id,
                    });
                    toast.success(t('secrets.deleteSuccess') || 'Secret deleted successfully');
                    secretsQuery.refetch();
                    setDeleteDialogOpen(false);
                    setSecretToDelete(null);
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : 'Failed to delete secret');
                  }
                }
              }}
              disabled={deleteSecretMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteSecretMutation.isPending ? 'Deleting...' : 'Delete Secret'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
